import React from 'react';
import { Helmet } from 'react-helmet-async';

// SEO configuration for the site
const seoConfig = {
  siteName: 'Neurolytics',
  siteUrl: 'https://neurolytics.cl',
  defaultTitle: 'Neurolytics | Soluciones IA y Automatizacion Chile',
  defaultDescription: 'Automatiza tu empresa con IA en Chile. Servicios de inteligencia artificial, analisis de datos y software a medida. Aumenta ventas 40%. Consulta gratis.',
  defaultImage: 'https://neurolytics.cl/images/og-image.jpg',
  twitterHandle: '@neurolytics_cl',
  locale: 'es_CL',
  themeColor: '#059669'
};

/**
 * SEO Component for dynamic meta tag management
 * @param {string} title - Page title (will be appended with site name)
 * @param {string} description - Page meta description
 * @param {string} image - Open Graph image URL
 * @param {string} url - Canonical URL for the page
 * @param {string} type - Open Graph type (default: website)
 * @param {object} structuredData - Additional JSON-LD structured data
 */
export const SEO = ({
  title,
  description,
  image,
  url,
  type = 'website',
  structuredData = null,
  noindex = false
}) => {
  const pageTitle = title
    ? `${title} | ${seoConfig.siteName}`
    : seoConfig.defaultTitle;

  const pageDescription = description || seoConfig.defaultDescription;
  const pageImage = image || seoConfig.defaultImage;
  const pageUrl = url || seoConfig.siteUrl;

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{pageTitle}</title>
      <meta name="description" content={pageDescription} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}

      {/* Canonical URL */}
      <link rel="canonical" href={pageUrl} />

      {/* Open Graph Tags */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={pageUrl} />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={pageDescription} />
      <meta property="og:image" content={pageImage} />
      <meta property="og:locale" content={seoConfig.locale} />
      <meta property="og:site_name" content={seoConfig.siteName} />

      {/* Twitter Card Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content={seoConfig.twitterHandle} />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={pageDescription} />
      <meta name="twitter:image" content={pageImage} />

      {/* Additional Structured Data */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}
    </Helmet>
  );
};

// Pre-configured SEO for specific pages
export const HomeSEO = () => (
  <SEO
    title={null} // Use default title for homepage
    description="Automatiza tu empresa con IA en Chile. Aumenta tus ventas 40% con inteligencia artificial, analisis de datos y software a medida. Consulta gratis en Neurolytics."
    url="https://neurolytics.cl/"
  />
);

export const InnAquaSEO = () => (
  <SEO
    title="InnAqua 2025 - Evento de Innovacion"
    description="Participa en InnAqua 2025, el evento de innovacion tecnologica en la industria acuicola. Descubre soluciones de IA y automatizacion para el sector."
    url="https://neurolytics.cl/innaqua"
    structuredData={{
      "@context": "https://schema.org",
      "@type": "Event",
      "name": "InnAqua 2025",
      "description": "Evento de innovacion tecnologica para la industria acuicola",
      "organizer": {
        "@type": "Organization",
        "name": "Neurolytics"
      },
      "location": {
        "@type": "Place",
        "name": "Los Lagos, Chile",
        "address": {
          "@type": "PostalAddress",
          "addressCountry": "CL"
        }
      }
    }}
  />
);

export const PrivacyPolicySEO = () => (
  <SEO
    title="Politica de Privacidad"
    description="Conoce como Neurolytics protege tu informacion personal. Nuestra politica de privacidad detalla el uso y proteccion de tus datos."
    url="https://neurolytics.cl/politica-privacidad"
    noindex={false}
  />
);

export const TermsOfServiceSEO = () => (
  <SEO
    title="Terminos de Servicio"
    description="Lee los terminos y condiciones de uso de los servicios de Neurolytics. Condiciones de contratacion de soluciones IA y automatizacion."
    url="https://neurolytics.cl/terminos-servicio"
    noindex={false}
  />
);

export default SEO;
